import { TruckOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import { FC, useCallback, useState } from "react";
import { formatDate, formatUAHNarrow } from "tm-shared/utils";

import {
    StyledNestedAccordion,
    StyledNestedAccordionWrapper,
} from "@/components/NestedAccordion/Styled";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { useAppSelector } from "@/hooks/redux";

import { EmptySalesResult } from "../EmptySalesResult";
import { InvoiceComment } from "./components/InvoiceComment";
import { InvoiceDetails } from "./components/InvoiceDetails";
import { InvoiceFlags } from "./components/InvoiceFlags";
import { ShipmentsModal } from "./components/ShipmentsModal";
import { ShipmentModalPayload } from "./components/ShipmentsModal/ShipmentsModal.types";
import { StyledSBMSum } from "./Styled";

interface ByMarketProps {
    targetDate: string;
}

export const ByMarket: FC<ByMarketProps> = ({ targetDate }) => {
    const { marketsSales } = useAppSelector((state) => state.work);

    const [shipmentModal, setShipmentModal] =
        useState<null | ShipmentModalPayload>(null);

    const handleCloseShipmentModal = useCallback(() => {
        setShipmentModal(null);
    }, []);

    const handleOpenShipmentModal = useCallback(
        ({
            marketId,
            marketName,
        }: {
            marketId: string;
            marketName: string;
        }) => {
            setShipmentModal({ marketId, targetDate, marketName });
        },
        [targetDate],
    );

    if (!marketsSales.length) return <EmptySalesResult />;

    return (
        <>
            <StyledNestedAccordionWrapper>
                <StyledNestedAccordion
                    accordion
                    level={0}
                    items={marketsSales.map(
                        ({ marketId, name, fop, address, invoices }) => {
                            const hasShipment = invoices.some(
                                (i) => !!i.shipmentDate,
                            );

                            return {
                                key: marketId,
                                label: (
                                    <Flex
                                        gap={8}
                                        align="stretch"
                                        justify="space-between"
                                    >
                                        <Flex vertical gap={6}>
                                            <PrimaryText fontSize={16}>
                                                ТТ {name}
                                            </PrimaryText>

                                            <PrimaryText fontSize={16}>
                                                {fop}
                                            </PrimaryText>

                                            <SecondaryText fontSize={14}>
                                                {address}
                                            </SecondaryText>
                                        </Flex>

                                        <Flex
                                            vertical
                                            align="center"
                                            gap={8}
                                            justify="space-between"
                                        >
                                            <PrimaryText fontSize={16}>
                                                {invoices.length}
                                            </PrimaryText>

                                            {hasShipment && (
                                                <Button
                                                    type="text"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenShipmentModal(
                                                            {
                                                                marketId,
                                                                marketName:
                                                                    name,
                                                            },
                                                        );
                                                    }}
                                                    icon={
                                                        <PrimaryText
                                                            fontSize={22}
                                                        >
                                                            <TruckOutlined />
                                                        </PrimaryText>
                                                    }
                                                />
                                            )}
                                        </Flex>
                                    </Flex>
                                ),
                                children: (
                                    <Flex
                                        style={{
                                            padding: "0 16px 0 20px",
                                        }}
                                    >
                                        <StyledNestedAccordion
                                            accordion
                                            isLast
                                            level={1}
                                            items={invoices.map((invoice) => {
                                                return {
                                                    key: invoice.id,
                                                    label: (
                                                        <Flex vertical gap={6}>
                                                            <PrimaryText
                                                                fontSize={16}
                                                            >
                                                                {invoice.id}
                                                            </PrimaryText>

                                                            <Flex
                                                                gap={16}
                                                                justify="space-between"
                                                            >
                                                                <SecondaryText>
                                                                    {formatDate(
                                                                        invoice.shipmentDate,
                                                                    )}
                                                                </SecondaryText>

                                                                <StyledSBMSum>
                                                                    {formatUAHNarrow(
                                                                        invoice.amount,
                                                                    )}
                                                                </StyledSBMSum>
                                                            </Flex>
                                                        </Flex>
                                                    ),
                                                    children: (
                                                        <>
                                                            <InvoiceDetails
                                                                invoice={
                                                                    invoice
                                                                }
                                                            />

                                                            <InvoiceFlags
                                                                invoice={
                                                                    invoice
                                                                }
                                                            />

                                                            <InvoiceComment
                                                                comments={
                                                                    invoice.comments
                                                                }
                                                            />
                                                        </>
                                                    ),
                                                };
                                            })}
                                        />
                                    </Flex>
                                ),
                            };
                        },
                    )}
                />
            </StyledNestedAccordionWrapper>

            {shipmentModal && (
                <ShipmentsModal
                    {...shipmentModal}
                    onClose={handleCloseShipmentModal}
                />
            )}
        </>
    );
};
